import React from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import PaymentMethodBrandIcon from './PaymentMethodBrandIcon';
import { CUSTOMER_PAYPAL_PAYMENT_HELP } from '../constants/paymentHelpText';

export interface BookingPaymentActionsProps {
  /** When true, only the Pay Now action is shown (matches Settings “Payment required at booking”). */
  paymentRequiredAtBooking?: boolean;
  /** When false, Pay Now is hidden (e.g. price on request with no fixed amount). */
  showPayNow?: boolean;
  onPayNow: () => void;
  /** Confirms booking without online payment (pay at visit). */
  onPayLater?: () => void;
  /** Formatted total shown on Pay Now when provided (e.g. "$42.00"). */
  payNowTotalLabel?: string;
  /** True while a booking submission is in flight — disables both buttons so a slow response can't be mistaken for "nothing happened" and tapped again. */
  submitting?: boolean;
  /**
   * When the business has connected verified PayPal API credentials, pass a
   * rendered <PayPalSmartButton /> here to replace the off-platform Pay Now
   * button — the booking only gets created after a real, captured payment.
   */
  verifiedPayPalButton?: React.ReactNode;
}

/**
 * Customer booking CTAs for 30-second booking confirmation.
 * Pay Now opens direct PayPal checkout (seller receives payment; BookAPT does not process funds).
 */
const BookingPaymentActions: React.FC<BookingPaymentActionsProps> = ({
  paymentRequiredAtBooking,
  showPayNow = true,
  onPayNow,
  onPayLater,
  payNowTotalLabel,
  submitting = false,
  verifiedPayPalButton,
}) => {
  return (
    <View style={styles.wrap}>
      {showPayNow && verifiedPayPalButton ? (
        verifiedPayPalButton
      ) : showPayNow ? (
        <TouchableOpacity
          style={[styles.payNowButton, submitting && styles.buttonDisabled]}
          onPress={onPayNow}
          disabled={submitting}
          testID="booking-pay-now"
          accessibilityRole="button"
          accessibilityLabel="Pay Now with PayPal"
        >
          <PaymentMethodBrandIcon methodId="paypal" size={28} display="symbol" />
          <View style={styles.payNowTextBlock}>
            <Text style={styles.payNowButtonText}>
              {submitting ? 'Processing…' : `Pay Now${payNowTotalLabel ? ` · ${payNowTotalLabel}` : ''}`}
            </Text>
            <Text style={styles.payNowSubtext}>PayPal or card on PayPal</Text>
          </View>
        </TouchableOpacity>
      ) : null}

      {!paymentRequiredAtBooking && onPayLater ? (
        <TouchableOpacity
          style={[styles.bookLaterButton, submitting && styles.buttonDisabled]}
          onPress={onPayLater}
          disabled={submitting}
          testID="booking-pay-later"
          accessibilityRole="button"
          accessibilityLabel="Confirm booking"
        >
          <Text style={styles.bookLaterButtonText}>{submitting ? 'Processing…' : 'Confirm booking'}</Text>
        </TouchableOpacity>
      ) : null}

      <Text style={styles.helpText}>{CUSTOMER_PAYPAL_PAYMENT_HELP}</Text>
    </View>
  );
};

const styles = StyleSheet.create({
  wrap: {
    gap: 12,
  },
  buttonDisabled: {
    opacity: 0.6,
  },
  payNowButton: {
    backgroundColor: '#0070ba',
    paddingVertical: 14,
    paddingHorizontal: 16,
    borderRadius: 12,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  payNowTextBlock: {
    flex: 1,
  },
  payNowButtonText: {
    color: 'white',
    fontSize: 18,
    fontWeight: 'bold',
  },
  payNowSubtext: {
    color: 'rgba(255,255,255,0.88)',
    fontSize: 13,
    marginTop: 2,
  },
  bookLaterButton: {
    backgroundColor: 'white',
    paddingVertical: 16,
    borderRadius: 12,
    alignItems: 'center',
    borderWidth: 2,
    borderColor: '#007AFF',
  },
  bookLaterButtonText: {
    color: '#007AFF',
    fontSize: 18,
    fontWeight: 'bold',
  },
  helpText: {
    fontSize: 12,
    color: '#666',
    lineHeight: 17,
    marginTop: 4,
  },
});

export default BookingPaymentActions;
